<script setup lang="ts">
import { computed } from 'vue'

import type { ContentfulNavigationItem } from '@fondof-ui/types'
import { localizeUrl } from '@shared/fondof-fn/url/localize'
import { useLocationStore } from '@shared/global-store/stores/location/location'
import HeaderNavSub from '@storefront-satch/components/organisms/navigation/HeaderNavSub.vue'

import { ChevronDownIcon } from '@fond-of/satch-icons'

const props = defineProps<{
  navLink?: ContentfulNavigationItem
  currentActive?: string
}>()

defineEmits<{
  (e: 'toggleMenu', entryId?: string): void
}>()
const locationStore = useLocationStore()

const hasItemsSubmenu = computed(
  () => props.navLink?.navigationItemsCollection?.items?.length
)

const isOpen = computed(() => props.navLink?.sys.id === props.currentActive)

const emitCurrentActive = computed(() => {
  if (
    props.currentActive !== undefined &&
    props.navLink?.sys.id === props.currentActive
  ) {
    return undefined
  }
  return props.navLink?.sys.id
})
</script>

<template>
  <li
    class="group/parent block w-auto list-none pl-0 pr-0 text-left lg:ml-[5px] lg:inline-block"
    :class="[
      navLink?.specialClass,
      { 'parent-active': isOpen },
      'lg:p-3.75 lg:pb-0',
    ]"
  >
    <span class="relative block">
      <a
        :class="[
          'font-sans-extrabold block basis-full text-[15px] leading-[21px] text-black',
          'p-3.75 lg:pb-3.75 pl-5 lg:p-0',
        ]"
        gtm-data-attr="mainnav"
        :gtm-data-label="navLink?.gtmDataLabel ?? navLink?.label"
        :href="localizeUrl(`/${navLink?.url || ''}`)"
      >
        <span class="font-sans-bold text-left lg:text-center lg:font-sans">
          {{ navLink?.label }}
        </span>
      </a>
      <button
        v-if="hasItemsSubmenu"
        aria-label="Toggle category menu"
        role="button"
        class="absolute -top-[1px] right-0 z-50 flex w-20 items-center justify-end py-4 pr-5 lg:hidden"
        @click="$emit('toggleMenu', emitCurrentActive)"
      >
        <ChevronDownIcon
          width="18px"
          height="16px"
          :class="{
            'rotate-180 transform': isOpen,
          }"
        />
      </button>
    </span>
    <HeaderNavSub
      v-if="hasItemsSubmenu"
      :submenu="navLink?.navigationItemsCollection?.items"
      :label="navLink?.label"
      :locale="locationStore.urlPrefix.urlPrefix"
    />
  </li>
</template>

<style lang="scss" scoped>
// Custom selector from contentful
.navigation__item--color-blue a > span {
  @apply text-primary;
  font-weight: 700;
}
</style>
